import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Warehouse, 
  Users, 
  Package, 
  TrendingUp, 
  AlertTriangle,
  LogOut,
  Plus,
  Search,
  ArrowRight
} from 'lucide-react';
import { getDocuments, createDocument } from '../services/firebase';
import { seedDailySalesDetails } from '../services/salesTransactions';
import { User, Product, InventoryAllocation, Sale } from '../types';

interface WarehouseDashboardProps {
  onShowInventory: () => void;
}

const WarehouseDashboard: React.FC<WarehouseDashboardProps> = ({ onShowInventory }) => {
  const { currentUser, logout } = useAuth();
  const [salesmen, setSalesmen] = useState<User[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [allocations, setAllocations] = useState<InventoryAllocation[]>([]);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [selectedSalesman, setSelectedSalesman] = useState<User | null>(null);
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedingMessage, setSeedingMessage] = useState('');
  const [stats, setStats] = useState({
    totalSalesmen: 0,
    totalProducts: 0,
    todaySales: 0,
    lowStockItems: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [salesmenData, productsData, salesData, allocationsData] = await Promise.all([
        getDocuments('users'),
        getDocuments('products'),
        getDocuments('sales'),
        getDocuments('inventory_allocations')
      ]);

      const salesmenUsers = salesmenData.filter((user: User) => user.role === 'salesman');
      setSalesmen(salesmenUsers);
      setProducts(productsData as Product[]);
      setSales(salesData as Sale[]);
      setAllocations(allocationsData as InventoryAllocation[]);

      // Calculate stats
      const todaySales = salesData.reduce((sum: number, sale: Sale) => sum + sale.grandTotal, 0);
      setStats({
        totalSalesmen: salesmenUsers.length,
        totalProducts: productsData.length,
        todaySales: todaySales,
        lowStockItems: 0 // Will be calculated from actual inventory
      });
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleAllocateInventory = async (salesmanId: string, productId: string, quantity: number) => {
    try {
      const salesman = salesmen.find(s => s.id === salesmanId);
      const product = products.find(p => p.id === productId);

      if (!salesman || !product) return;

      const allocationData = {
        salesmanId: salesmanId,
        salesmanName: salesman.name,
        productId: productId,
        productName: product.name,
        allocatedQty: quantity,
        remainingQty: quantity,
        allocatedDate: new Date(),
        warehouseUserId: currentUser?.id || ''
      };

      await createDocument('inventory_allocations', allocationData);
      setShowAllocateModal(false);
      loadData();
    } catch (error) {
      console.error('Error allocating inventory:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getSalesmanSales = (salesmanId: string) => {
    return sales.filter(sale => sale.salesmanId === salesmanId);
  };

  const getSalesmanTotal = (salesmanId: string) => {
    return getSalesmanSales(salesmanId).reduce((sum, sale) => sum + sale.grandTotal, 0);
  };

  const handlePrepareDailySales = async () => {
    if (!currentUser?.warehouseId) {
      setSeedingMessage('User not associated with a warehouse');
      return;
    }

    setIsSeeding(true);
    setSeedingMessage('Preparing daily sales data...');

    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const result = await seedDailySalesDetails(
        today,
        currentUser.warehouseId,
        currentUser.id
      );

      if (result.success) {
        setSeedingMessage(`✅ ${result.message}`);
        setTimeout(() => setSeedingMessage(''), 3000);
      } else {
        setSeedingMessage(`❌ ${result.message}`);
        setTimeout(() => setSeedingMessage(''), 5000);
      }
    } catch (error) {
      setSeedingMessage(`❌ Error: ${error}`);
      setTimeout(() => setSeedingMessage(''), 5000);
    } finally {
      setIsSeeding(false);
    }
  };

  return (
    <div className="mobile-container bg-gray-50 min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center">
              <Warehouse className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-800">Warehouse Dashboard</h1>
              <p className="text-sm text-gray-600">Welcome, {currentUser?.name}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <LogOut className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Salesmen</p>
                <p className="text-2xl font-bold text-blue-600">{stats.totalSalesmen}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Products</p>
                <p className="text-2xl font-bold text-green-600">{stats.totalProducts}</p>
              </div>
              <Package className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Today's Sales</p>
                <p className="text-2xl font-bold text-purple-600">₹{stats.todaySales.toLocaleString()}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Low Stock Items</p>
                <p className="text-2xl font-bold text-orange-600">{stats.lowStockItems}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <button 
              onClick={onShowInventory}
              className="flex flex-col items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <Package className="w-8 h-8 text-blue-600 mb-2" />
              <span className="text-sm font-medium text-gray-700">Enhanced Inventory</span>
            </button>
            
            <button 
              onClick={() => setShowAllocateModal(true)}
              className="flex flex-col items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
            >
              <Plus className="w-8 h-8 text-green-600 mb-2" />
              <span className="text-sm font-medium text-gray-700">Van Loading</span>
            </button>
          </div>

          {/* Daily Sales Preparation */}
          <div className="mt-4">
            <button 
              onClick={handlePrepareDailySales}
              disabled={isSeeding}
              className="w-full flex flex-col items-center p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <TrendingUp className="w-8 h-8 text-orange-600 mb-2" />
              <span className="text-sm font-medium text-gray-700">
                {isSeeding ? 'Preparing...' : 'Prepare Daily Sales'}
              </span>
            </button>
            {seedingMessage && (
              <div className="mt-2 p-2 text-xs bg-gray-100 rounded text-center">
                {seedingMessage}
              </div>
            )}
          </div>
          
          {/* New Zoho Books Integration Actions */}
          <div className="mt-4 p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-purple-900">Zoho Books Integration</h4>
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            </div>
            <p className="text-xs text-purple-700 mb-3">Real-time inventory sync with Zoho Books</p>
            <div className="grid grid-cols-2 gap-2">
              <button className="text-xs px-3 py-2 bg-white bg-opacity-80 text-purple-700 rounded border border-purple-200 hover:bg-opacity-100 transition-colors">
                📊 Sync Now
              </button>
              <button className="text-xs px-3 py-2 bg-white bg-opacity-80 text-purple-700 rounded border border-purple-200 hover:bg-opacity-100 transition-colors">
                📋 View Journal
              </button>
            </div>
          </div>
        </div>

        {/* Salesmen Performance */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Salesmen Performance</h3>
          <div className="space-y-3">
            {salesmen.map((salesman) => (
              <div key={salesman.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {salesman.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{salesman.name}</p>
                    <p className="text-sm text-gray-600">{salesman.email}</p>
                  </div>
                </div>
                <div className="text-right flex items-center space-x-2">
                  <div>
                    <p className="text-sm font-medium text-green-600">
                      ₹{getSalesmanTotal(salesman.id).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">
                      {getSalesmanSales(salesman.id).length} sales
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Allocate Inventory Modal */}
      {showAllocateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Allocate Inventory</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Salesman
                </label>
                <select 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onChange={(e) => {
                    const salesman = salesmen.find(s => s.id === e.target.value);
                    setSelectedSalesman(salesman || null);
                  }}
                >
                  <option value="">Choose a salesman</option>
                  {salesmen.map(salesman => (
                    <option key={salesman.id} value={salesman.id}>
                      {salesman.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Product
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="">Choose a product</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} ({product.unit})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity to Allocate
                </label>
                <input
                  type="number"
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter quantity"
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowAllocateModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Allocate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseDashboard;
